import {gql} from '@apollo/client';
import {useEffect} from 'react';

import {warnAndRedirectAfterInaccessiblePageOpen} from 'auth/capabilities/InaccessibleFeaturesActions';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {GENERIC_SHORT_ERROR_MESSAGE} from 'ui-library/ErrorConstants';

import {
  CanCreateAaMonomersQuery,
  CanCreateAaMonomersQueryVariables,
} from './__generated/CanCreateAaMonomersQuery';

const CAN_CREATE_AA_MONOMERS_QUERY = gql`
  query CanCreateAaMonomersQuery @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    currentTenant {
      id
      permissions {
        canCreateAaMonomers
      }
    }
  }
`;

export type UseCanCreateAaMonomersQueryResult =
  | {
      status: QueryResultType.SUCCESS;
      canCreateAaMonomers: boolean;
    }
  | {
      status: QueryResultType.ERROR | QueryResultType.PENDING;
      canCreateAaMonomers: null;
    };

export function useCanCreateAaMonomersQuery(): UseCanCreateAaMonomersQueryResult {
  const result = useBenchlingQuery<CanCreateAaMonomersQuery, CanCreateAaMonomersQueryVariables>(
    CAN_CREATE_AA_MONOMERS_QUERY
  );

  if (result.type !== QueryResultType.SUCCESS) {
    return {status: result.type, canCreateAaMonomers: null};
  }

  return {
    status: QueryResultType.SUCCESS,
    canCreateAaMonomers: result.data.currentTenant.permissions.canCreateAaMonomers,
  };
}

interface UseCanCreateAaMonomersAndRedirectOptions {
  viewOnly?: boolean;
}

/**
 * Wraps useCanCreateAaMonomersQuery with redirect side effects: redirects with a
 * toast if the user lacks access or the query fails. Returns whether the
 * check is still in progress (callers should show a throbber while true).
 */
export function useCanCreateAaMonomersAndRedirect({
  viewOnly = false,
}: UseCanCreateAaMonomersAndRedirectOptions = {}): {isLoading: boolean} {
  const {status, canCreateAaMonomers} = useCanCreateAaMonomersQuery();
  const isLoading = status === QueryResultType.PENDING;
  const shouldRedirect =
    !viewOnly && !isLoading && (status === QueryResultType.ERROR || !canCreateAaMonomers);

  useEffect(() => {
    if (!shouldRedirect) {
      return;
    }
    if (status === QueryResultType.ERROR) {
      warnAndRedirectAfterInaccessiblePageOpen(GENERIC_SHORT_ERROR_MESSAGE);
    } else {
      warnAndRedirectAfterInaccessiblePageOpen(
        'You do not have access to this feature. Please contact your tenant admin for more information.'
      );
    }
  }, [shouldRedirect, status]);

  return {isLoading: isLoading || shouldRedirect};
}
